import React from 'react';
import { BaseScanGridWidget } from './BaseScanGridWidget';
import { 
    extractInfrastructureFindings, 
    DEFAULT_INFRASTRUCTURE_CATEGORIES,
    evaluateDataSourceConfig 
} from './KeyFindingsAdvancedWidgetHelper';
import { 
    extractManagedServiceFindings, 
    DEFAULT_MANAGED_SERVICE_CATEGORIES,
    getManagedServiceStatusDescription
} from './ManagedServiceExperienceWidgetHelper';
import { 
    extractSecurityFindings, 
    DEFAULT_SECURITY_CATEGORIES,
    getSecurityStatusDescription
} from './SecurityScanGridWidgetHelper';
import { 
    extractCyberSecurityFindings, 
    DEFAULT_CYBER_SECURITY_CATEGORIES,
    getCyberSecurityStatusDescription
} from './CyberSecurityGridWidgetHelper';
import { 
    getInfrastructureStatusDescription 
} from './KeyFindingsAdvancedWidgetHelper';
import { 
    recreateFindingsArray,
    matchesScanType,
    SCAN_TYPE_MAPPING,
    MANDATORY_CATEGORIES
} from './FindingsWidgets';

/**
 * AdvancedScanGridWidget
 * A highly configurable widget that can show findings from any scan type.
 * Supports presets for Infrastructure, Managed IT, Security, Cyber Security, 
 * Network Scan, Localhost Scan, AD Scan, MS365 Scan, and PII Scan.
 */
export const AdvancedScanGridWidget = ({ data, config = {}, style = {}, onConfigChange, theme = {}, scanGroups = [], availableCategories = [] }) => {
    const { gridType = 'custom', title: configTitle, dataOverrides = {} } = config;

    // 1. Determine which logic to use based on gridType
    let extractFn = null;
    let defaultCategories = [];
    let defaultTitle = 'ADVANCED SCAN GRID';
    let categoryName = 'Advanced';

    // Bridge legacy keys to backend scan_type keys
    const legacyMap = {
        'infrastructure': 'Infrastructure',
        'managed-service': 'Managed IT/Service Experience',
        'security': 'Security',
        'cyber-security': 'Cyber Security (Threat & Exposure)',
        'network-scan': 'Network Scan',
        'localhost-scan': 'Localhost Scan',
        'ad-scan': 'AD Scan',
        'ms365-scan': 'MS365 Scan',
        'pii-scan': 'PII Scan'
    };

    const backendKey = legacyMap[gridType] || gridType;
    const group = scanGroups.find(g => g.key === backendKey);

    if (group && gridType !== 'custom') {
        defaultTitle = group.label.toUpperCase();
        categoryName = group.label;
        // Resolve dynamic defaults from availableCategories
        defaultCategories = availableCategories
            .filter(cat => cat.scan_type === group.key)
            .map(cat => ({ id: cat.name, label: cat.name }));
    }

    // Helper to normalize status for BaseScanGridWidget (Critical -> Red, etc.)
    const normalizeStatus = (rawStatus) => {
        if (!rawStatus) return 'Unknown';
        if (rawStatus === 'Red' || rawStatus === 'Yellow' || rawStatus === 'Green' || rawStatus === 'Unknown') return rawStatus;
        
        if (rawStatus === 'Critical' || rawStatus === 'Bad') return 'Red';
        if (rawStatus === 'Warning' || rawStatus === 'Moderate') return 'Yellow';
        if (rawStatus === 'Healthy' || rawStatus === 'Good' || rawStatus === 'Pass') return 'Green';
        
        return 'Unknown';
    };

    // Helper to extract findings for standard scan types using recreateFindingsArray
    const extractStandardScanFindings = (data, scanType) => {
        const allFindings = recreateFindingsArray(data);
        return allFindings
            .filter(f => matchesScanType(f, scanType))
            .map(f => ({
                id: f[0], // Using name as ID for these standard types
                title: f[0],
                status: normalizeStatus(f[1]),
                score: f[2],
                description: f[3],
                metrics: f[5] ? Object.entries(f[5]).map(([label, value]) => ({ label, value })) : [],
                category: f[6] || scanType
            }));
    };

    switch (gridType) {
        case 'managed-service':
            extractFn = extractManagedServiceFindings;
            defaultCategories = DEFAULT_MANAGED_SERVICE_CATEGORIES;
            break;
        case 'security':
            extractFn = extractSecurityFindings;
            defaultCategories = DEFAULT_SECURITY_CATEGORIES;
            break;
        case 'cyber-security':
            extractFn = extractCyberSecurityFindings;
            defaultCategories = DEFAULT_CYBER_SECURITY_CATEGORIES;
            break;
        case 'infrastructure':
            extractFn = extractInfrastructureFindings;
            defaultCategories = DEFAULT_INFRASTRUCTURE_CATEGORIES;
            break;
        case 'network-scan':
        case 'localhost-scan':
        case 'ad-scan':
        case 'ms365-scan':
        case 'pii-scan':
            extractFn = (d) => extractStandardScanFindings(d, backendKey);
            break;
        case 'custom':
            extractFn = () => [];
            defaultCategories = [];
            defaultTitle = 'CUSTOM SCAN GRID';
            categoryName = 'Custom';
            break;
        default:
            // Dynamic group support
            if (group) {
                extractFn = (d) => extractStandardScanFindings(d, group.key);
            } else {
                extractFn = () => [];
            }
            break;
    }

    const title = configTitle || defaultTitle;

    // 2. Extract base findings
    // 2. Extract base findings for the current grid type
    const localBaseFindings = extractFn ? extractFn(data, config) : [];
    const baseFindingsMap = localBaseFindings.reduce((acc, f) => {
        acc[f.id] = f;
        if (f.title && f.title !== f.id) acc[f.title] = f; // also index by display label
        return acc;
    }, {});

    // 2b. Aggregate ALL base findings from all specialized extractors as a global fallback
    // This allows "Custom" grids to resolve specific logic IDs like 'infrastructure-1'
    const globalBaseFindingsMap = (() => {
        // Only build this if we are in custom mode or if we need a fallback
        const all = [
            ...extractManagedServiceFindings(data, config),
            ...extractSecurityFindings(data, config),
            ...extractCyberSecurityFindings(data, config),
            ...extractInfrastructureFindings(data, config),
            ...(scanGroups || []).map(g => extractStandardScanFindings(data, g.label)).flat(),
        ];
        return all.reduce((acc, f) => {
            acc[f.id] = f;           // keyed by internal ID (e.g. 'cyber-security-1')
            if (f.title && f.title !== f.id) acc[f.title] = f; // also keyed by display label
            return acc;
        }, {});
    })();

    // 3. Define the discovery pool (all findings from all types)
    const discoveryPool = recreateFindingsArray(data);
    console.log('[AdvancedScanGridWidget] discoveryPool size:', discoveryPool.length);
    
    const discoveryMap = discoveryPool.reduce((acc, f) => {
        const [name, rawStatus, score, description, , metrics, explicitScanType] = f;
        
        // Normalize status for BaseScanGridWidget (Critical -> Red, etc.)
        const status = normalizeStatus(rawStatus);

        const scanType = explicitScanType || SCAN_TYPE_MAPPING[name] || 'Other';

        acc[name] = {
            id: name,
            title: name,
            status,
            score,
            description,
            metrics: metrics ? Object.entries(metrics).map(([label, value]) => ({ label, value })) : [],
            category: scanType
        };
        return acc;
    }, {});
    console.log('[AdvancedScanGridWidget] discoveryMap keys:', Object.keys(discoveryMap).length);

    // 4. Define status descriptions for cycling
    const getStatusDescription = (finding, status) => {
        // Find which logic to use based on the finding's category
        const cat = finding.category || categoryName;
        if (cat === 'Infrastructure' || cat === 'Infrastructure Scan Grid') return getInfrastructureStatusDescription(finding.id, status) || finding.description;
        if (cat === 'Managed Service Experience' || cat === 'Managed IT') return getManagedServiceStatusDescription(finding.id, status) || finding.description;
        if (cat === 'Security' || cat === 'Security Scan Grid') return getSecurityStatusDescription(finding.id, status) || finding.description;
        if (cat === 'Threat & Exposure' || cat === 'Cyber Security') return getCyberSecurityStatusDescription(finding.id, status) || finding.description;
        
        // Default descriptions for Custom or generic categories
        if (status === 'Green') return 'Requirement satisfied or status is healthy';
        if (status === 'Yellow') return 'Attention suggested or partial compliance';
        if (status === 'Red') return 'Immediate action required or non-compliant';
        if (status === 'Unknown') return 'No data available to determine status';

        return finding.description;
    };

    // 4. Handle card click for manual override
    const handleCardClick = (finding) => {
        if (!onConfigChange) return;

        const statusCycle = ['Green', 'Yellow', 'Red', 'Unknown'];
        const currentStatus = finding.status || 'Unknown';
        const nextStatus = statusCycle[(statusCycle.indexOf(currentStatus) + 1) % statusCycle.length];
        
        let nextScore = 0;
        if (nextStatus === 'Green') nextScore = 100;
        else if (nextStatus === 'Yellow') nextScore = 50;
        
        onConfigChange({
            ...config,
            dataOverrides: {
                ...dataOverrides,
                [finding.id]: {
                    status: nextStatus,
                    score: nextScore,
                    description: getStatusDescription(finding, nextStatus),
                    mode: 'manual'
                }
            }
        });
    };

    // 5. Build final findings list
    // Use config.categories if available, otherwise fallback to defaults for the selected type
    const categories = config?.categories || defaultCategories;
    
    // If it's a standard scan type and no categories are set, we can try to auto-fill the first 9 from baseFindings
    const effectiveCategories = (categories.length === 0 && localBaseFindings.length > 0 && gridType !== 'custom')
        ? localBaseFindings.slice(0, 9).map(f => ({ id: f.id, label: f.title }))
        : categories;

    const allFinalFindings = effectiveCategories.map(cat => {
        const override = dataOverrides[cat.id] || {};
        const mode = override.mode || 'auto';
        
        // Resolve the "source" finding logic
        let sourceFinding = null;
        if (mode === 'auto') {
            const explicitId = override.autoSourceId;
            const explicitType = override.autoSourceType;

            // Helper to resolve finding with fuzzy matching for standard scan categories
            const resolveFinding = (id, label, type) => {
                if (!id) return null;
                
                // 1. Exact match in any of our maps (by ID)
                let found = discoveryMap[id] || baseFindingsMap[id] || globalBaseFindingsMap[id];
                if (found) return found;

                // 1b. Exact match by Label (Smart Fallback)
                if (label) {
                    found = discoveryMap[label] || baseFindingsMap[label] || globalBaseFindingsMap[label];
                    if (found) return found;
                }

                // 2. Fuzzy match for standard categories (e.g., "Antivirus" -> "Antivirus (Localhost Scan)")
                // This handles cases where selection IDs are clean/generic but findings are qualified
                if (type) {
                    const fuzzyMatch = Object.values(discoveryMap).find(f => 
                        matchesScanType(f, type) && (
                            f.title === id || f.title.startsWith(`${id} `) ||
                            (label && (f.title === label || f.title.startsWith(`${label} `)))
                        )
                    ) || Object.values(globalBaseFindingsMap).find(f =>
                        matchesScanType(f, type) && (
                            f.title === id || f.title.startsWith(`${id} `) ||
                            (label && (f.title === label || f.title.startsWith(`${label} `)))
                        )
                    );
                    if (fuzzyMatch) return fuzzyMatch;
                }

                // 3. Stub matching: if it's a known mandatory category but not in results, return a placeholder
                const lookupId = id || label;
                if (lookupId && MANDATORY_CATEGORIES.includes(lookupId)) {
                    return {
                        id: lookupId,
                        title: lookupId,
                        status: 'Unknown',
                        score: 0,
                        description: 'No data available for this scan category.',
                        metrics: [],
                        category: type || SCAN_TYPE_MAPPING[lookupId] || 'Scan Result'
                    };
                }
                
                return null;
            };

            if (explicitId && explicitType) {
                // If the item has a specific cross-type source, use the global pool with fuzzy matching
                sourceFinding = resolveFinding(explicitId, cat.label, explicitType);
            } else if (explicitId) {
                // If only ID is provided, try fuzzy resolution
                sourceFinding = resolveFinding(explicitId, cat.label, null);
            } else {
                // Traditional fallback for default templates (checked via ID or Label)
                sourceFinding = baseFindingsMap[cat.id] || globalBaseFindingsMap[cat.id] || (cat.label ? (baseFindingsMap[cat.label] || globalBaseFindingsMap[cat.label]) : null);
                // Final fallback: try discovery map if cat.id is a recognizable finding name
                if (!sourceFinding) {
                    sourceFinding = resolveFinding(cat.id, cat.label, null);
                }
            }
        }

        const baseFinding = {
            id: cat.id,
            title: sourceFinding ? sourceFinding.title : (cat.label || 'New Item'),
            originalTitle: sourceFinding ? sourceFinding.title : (cat.label || 'New Item'),
            status: sourceFinding ? sourceFinding.status : 'Unknown',
            score: sourceFinding ? sourceFinding.score : 0,
            description: sourceFinding ? sourceFinding.description : 'Configure this item',
            metrics: sourceFinding ? sourceFinding.metrics : [],
            category: sourceFinding ? sourceFinding.category : categoryName
        };

        let finalFinding = { ...baseFinding, isManual: false };

        if (override && (override.status || override.mode === 'manual')) {
            const statusToScore = { 'Green': 100, 'Yellow': 50, 'Red': 0, 'Unknown': 0 };
            finalFinding = {
                ...baseFinding,
                status: override.status,
                score: override.score !== undefined ? override.score : (statusToScore[override.status] || 0),
                description: override.description || getStatusDescription(baseFinding, override.status),
                isManual: true
            };
        } else if (override && override.mode === 'dataSource') {
            const result = evaluateDataSourceConfig(data, override);
            finalFinding = {
                ...baseFinding,
                status: result.status,
                score: result.score,
                description: result.description,
                metrics: result.metrics.length > 0 ? result.metrics : baseFinding.metrics,
                isDataSource: true
            };
        }

        // Variable replacement helper for notes
        const applyVariables = (noteTemplate, finding) => {
            if (!noteTemplate) return noteTemplate;
            let note = noteTemplate;
            
            // {value} -> score
            note = note.replace(/{value}/g, Math.round(finding.score));
            
            // Map metrics ({count}, {total}, {percent})
            if (finding.metrics && finding.metrics.length > 0) {
                finding.metrics.forEach(m => {
                    const label = (m.label || '').toLowerCase();
                    if (label === 'count' || label === 'supported' || label === 'active' || label === 'matching') {
                        note = note.replace(/{count}/g, m.value);
                    } else if (label === 'total') {
                        note = note.replace(/{total}/g, m.value);
                    } else if (label === 'percentage' || label === 'percent' || label === 'ratio') {
                        note = note.replace(/{percent}/g, m.value);
                    }
                    // Direct dynamic replacement for specified label name
                    note = note.replace(new RegExp(`{${label}}`, 'g'), m.value);
                });
            }
            return note;
        };

        // Apply Threshold Note Customization
        const customNotes = override?.customNotes || {};
        const currentStatus = finalFinding.status; // Red, Yellow, Green, Unknown
        const tierMap = { 'Green': 'Good', 'Yellow': 'Moderate', 'Red': 'Bad' };

        // Map normalized status back to Critical/Warning/Healthy keys used in Properties Panel
        const statusToNoteKey = { 'Red': 'Critical', 'Yellow': 'Warning', 'Green': 'Healthy' };
        const noteKey = statusToNoteKey[currentStatus] || currentStatus;
        
        let finalDescription = finalFinding.description;

        // 1. Check for manual/template override from properties panel
        if (customNotes[noteKey]) {
            finalDescription = applyVariables(customNotes[noteKey], finalFinding);
        } 
        // 2. Fallback to legacy single description
        else if (override?.description && (!override.mode || override.mode === 'auto')) {
            finalDescription = override.description;
        }
        // 3. Automated Fallback: Try industry-standard notes from system thresholds
        else if (data?.scanData?.category_thresholds || data?.category_thresholds) {
            const thresholds = data?.scanData?.category_thresholds || data?.category_thresholds;
            const catName = finalFinding.originalTitle || finalFinding.title;
            const lowerCatName = catName?.trim().toLowerCase();

            // Robust lookup (Exact -> Case-insensitive -> Partial)
            let threshold = thresholds[catName];
            if (!threshold) {
                const match = Object.entries(thresholds).find(([k]) => {
                    const key = k.trim().toLowerCase();
                    return key === lowerCatName || key.includes(lowerCatName) || lowerCatName.includes(key);
                });
                if (match) threshold = match[1];
            }

            if (threshold) {
                const tier = tierMap[currentStatus];
                // Try both 'Healthy' and 'Good' if status is Green
                const tierKeys = tier === 'Good' ? ['Good', 'Healthy'] : [tier];
                let defaultNoteTemplate = null;

                for (const tKey of tierKeys) {
                    const rules = threshold.rules?.[tKey] || {};
                    const defRules = threshold.default_rules?.[tKey] || {};

                    // Priority: 1. rules.note -> 2. rules.notes[0] -> 3. default_rules.note -> 4. default_rules.notes[0]
                    defaultNoteTemplate = rules.note || 
                                          (Array.isArray(rules.notes) && rules.notes[0]?.text) ||
                                          defRules.note || 
                                          (Array.isArray(defRules.notes) && defRules.notes[0]?.text);
                    
                    if (defaultNoteTemplate) break;
                }

                if (defaultNoteTemplate) {
                    finalDescription = applyVariables(defaultNoteTemplate, finalFinding);
                }
            }
        }
        
        finalFinding.description = finalDescription;

        // Title resolution logic:
        // 1. Manual displayName override
        // 2. Resolved sourceFinding title (e.g. "Antivirus")
        // 3. cat.label if it's not the generic placeholder
        if (override?.displayName) {
            finalFinding.title = override.displayName;
        } else if (sourceFinding?.title) {
            finalFinding.title = sourceFinding.title;
        } else if (cat.label) {
            finalFinding.title = cat.label;
        }

        return finalFinding;
    });

    // Allow all findings for Advanced Scan Grid (no 9-item limit like basic grids)
    const finalFindings = allFinalFindings;

    // 6. Calculate audit score (for all items)
    const calculateAuditScore = () => {
        const knownFindings = finalFindings.filter(f => f.status !== 'Unknown');
        if (knownFindings.length === 0) return 0;
        const scores = knownFindings.map(f => f.score);
        const avgScore = scores.reduce((a, b) => a + b, 0) / scores.length;
        return Number.isInteger(avgScore) ? avgScore : parseFloat(avgScore.toFixed(2));
    };

    const auditScore = calculateAuditScore();


    return (
        <BaseScanGridWidget
            findings={finalFindings}
            title={title}
            auditScore={auditScore}
            onCardClick={handleCardClick}
            style={style}
            config={config}
            theme={theme}
        />
    );
};
